<template>
  <div class="py-4 container-fluid">
    <transition name="banner-slide">
        <div v-if="isSyncing || progress === 100">
            <RvoSyncBanner />
        </div>
    </transition>

    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-header p-3 pt-2 d-flex align-items-center">
            <div
              class="icon icon-lg icon-shape shadow text-center border-radius-xl bg-gradient-success shadow-dark me-3"
              style="margin-top: -1.5rem">
              <i class="material-symbols-rounded opacity-10 text-white" aria-hidden="true">add_business</i>
            </div>

            <div class="d-flex align-items-center">
              <h6 class="mb-0 me-3">{{ $t('mating.Add_newbord_to_ubn') }}</h6>

              <p v-if="state.noEartagAvailable" class="text-danger text-sm mb-0">
                {{ state.rVOMessage }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <form>

          <div
            v-for="(animal, index) in animalForms"
            :key="index"
            class="animal-entry card mt-4 pt-3"
            data-animation="FadeIn"
          >

            <div class="card-header" style="padding: 0px; margin-left: 23px;">
              <h6>{{ $t('animal.Lamb') }} {{ index + 1 }}</h6>
            </div>

            <div class="card-body pt-0">
              <div class="row mb-3">

                <!-- Eartag -->
                <div class="col-md-3">
                  <div class="input-group input-group-static">
                    <label>{{ $t('animal.Eartag') }} *</label>

                    <template v-if="animal.isSaved">
                      <p class="form-control-plaintext">{{ animal.tagNumber }}</p>
                    </template>

                    <template v-else>
                      <select
                        :id="`tagId-${index}`"
                        v-model="animal.tagId"
                        class="form-control"
                      >
                        <option value="">{{ $t('animal.Select_tag_number') }}</option>
                        <option
                          v-for="item in availableTagsForRow(index)"
                          :key="item.id"
                          :value="item.id"
                        >
                          {{ item.tagNumber }}
                        </option>
                      </select>

                      <span
                        v-for="err in v$[index]?.tagId?.$errors"
                        :key="err.$uid"
                        class="text-sm text-danger d-block"
                      >
                        {{ err.$message }}
                      </span>
                    </template>
                  </div>
                </div>

                <!-- Gender -->
                <div class="col-md-3">
                  <div class="input-group input-group-static">
                    <label>{{ $t('animal.Gender') }} *</label>

                    <template v-if="animal.isSaved">
                      <p class="form-control-plaintext">
                        {{ state.genders.find(g => g.id === animal.genderId)?.description }}
                      </p>
                    </template>

                    <template v-else>
                      <select
                        :id="`genderId-${index}`"
                        v-model="animal.genderId"
                        class="form-control"
                      >
                        <option value="">{{ $t('animal.Select_gender') }}</option>
                        <option
                          v-for="item in state.genders"
                          :key="item.id"
                          :value="item.id"
                        >
                          {{ item.description }}
                        </option>
                      </select>

                      <span
                        v-if="v$[index]?.genderId?.$error"
                        class="text-sm text-danger"
                      >
                        {{ v$[index]?.genderId?.$errors[0]?.$message }}
                      </span>
                    </template>
                  </div>
                </div>

                <!-- Name -->
                <div class="col-md-3">
                  <div class="input-group input-group-static">
                    <label>{{ $t('animal.Name') }}</label>

                    <template v-if="animal.isSaved">
                      <p class="form-control-plaintext">{{ animal.name }}</p>
                    </template>

                    <template v-else>
                      <input
                        v-model="animal.name"
                        type="text"
                        class="form-control"
                        :placeholder="$t('animal.Name')"
                      />
                    </template>
                  </div>
                </div>

                <!-- Colour -->
                <div class="col-md-3">
                  <div class="input-group input-group-static">
                    <label>{{ $t('animal.Colour') }}</label>

                    <template v-if="animal.isSaved">
                      <p class="form-control-plaintext">
                        {{ state.colours.find(c => c.id === animal.colorId)?.name }}
                      </p>
                    </template>

                    <template v-else>
                      <select
                        :id="`colorId-${index}`"
                        v-model="animal.colorId"
                        class="form-control"
                      >
                        <option value="">{{ $t('animal.Select_colour') }}</option>
                        <option
                          v-for="item in state.colours"
                          :key="item.id"
                          :value="item.id"
                        >
                          {{ item.name }}
                        </option>
                      </select>
                    </template>
                  </div>
                </div>

              </div>
            </div>

            <!-- PER ROW SAVE BUTTON - ONLY SHOWN WHEN NOT SAVED -->
            <div class="d-flex justify-content-end p-3" v-if="!animal.isSaved">
              <button
                type="button"
                class="btn btn-sm bg-gradient-success"
                :disabled="animal.isProcessing"
                @click="submitSingleAnimal(index)"
              >
                {{ $t('common.Save') }}
              </button>
            </div>

          </div>

          <!-- ONLY CANCEL BUTTON AT BOTTOM -->
          <div class="d-flex justify-content-end mt-3">
            <button
              type="button"
              class="btn bg-gradient-danger btn-sm me-2"
              @click="onCancel"
            >
              {{ $t('common.Cancel') }}
            </button>
          </div>

        </form>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick, watch } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import axios from "axios";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { showError, showSuccess } from "../../helpers/alerts";
import RvoSyncBanner from "@/components/RvoSyncBanner.vue";

export default {
  name: "AddNewBornPage",
  components: { RvoSyncBanner },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const animalForms = ref([]);

    const state = reactive({
      genders: [],
      colours: [],
      tagNumbers: [],

      matingId: "",
      ubnId: "",
      birthDate: "",

      noEartagAvailable: false,
      rVOMessage: "",
    });

    const createAnimalForm = () => ({
      name: "",
      colorId: "",
      genderId: "",
      tagId: "",
      tagNumber: "",
      tagWorkNumber: "",

      isSaved: false,
      isProcessing: false,
      animalId: null,
      animalWorkNumber: null
    });

    // Keep tagNumber and workNumber in sync with selected tag
    watch(
      animalForms,
      () => {
        animalForms.value
          .filter(a => !a.isSaved)        // ONLY update unsaved rows
          .forEach(a => {
            const found = state.tagNumbers.find(x => x.id === a.tagId);
            a.tagNumber = found?.tagNumber || "";
            a.tagWorkNumber = found?.workNumber || "";
          });
      },
      { deep: true }
    );

    // Validation rules per row
    const rules = computed(() =>
      animalForms.value.map(() => ({
        tagId: { required },
        genderId: { required },
      }))
    );

    const v$ = useVuelidate(rules, animalForms);

    const isSyncing = computed(() => store.state.rvoSync.isSyncing);
    const progress = computed(() => store.state.rvoSync.progress);

    const availableTagsForRow = (index) => {
      const selected = animalForms.value
        .map((a, i) => (i !== index ? a.tagId : null))
        .filter(Boolean);

      return state.tagNumbers.filter(
        tag =>
          !selected.includes(tag.id) ||
          animalForms.value[index].tagId === tag.id
      );
    };

    const fetchOptions = async () => {
      try {
        const refs = {
          genders: "9a1d69fe-c91f-4634-80cb-9f56db8d2a46",
          colours: "6d58593a-62f2-4fed-93b9-84eaabbbf47a",
        };

        for (const [key, id] of Object.entries(refs)) {
          const res = await axios.get(
            `/reference/getreference?referenceTypeId=${id}`
          );
          state[key] = res.data?.data || [];
        }

        const role = store.state.roleName;
        const relation = store.state.keeperRVORelationNumber;

        const url =
          role === "Keeper"
            ? `/eartag/getbyrvorelationnumber?rvorelationnumber=${relation}`
            : "eartag/getall";

        const res = await axios.get(url);

        state.tagNumbers = Array.isArray(res.data?.data)
          ? res.data.data
          : [];
      } catch {
        showError(t("errors.Something_went_wrong"));
      }
    };

    const fetchNewBornData = async (id) => {
      try {
        const res = await axios.get(`mating/${id}`);

        if (!res.data.success) {
          showError(t("errors.Contact_system_admin"));
          return;
        }

        const mating = res.data.data;

        state.matingId = mating.id;
        state.ubnId = mating.ubnId;
        state.birthDate = mating.birthDate;

        animalForms.value = [];

        if (animalForms.value.length === 0) {
          for (let i = 0; i < mating.noOfAlive; i++) {
            animalForms.value.push(createAnimalForm());
          }
        }

        await nextTick();
      } catch {
        showError(t("errors.Something_went_wrong"));
      }
    };

    const syncRVOEartag = async (id) => {
      try {

        // Skip if all newborns already saved
        const allSaved = animalForms.value.length > 0 &&
          animalForms.value.every(a => a.isSaved === true);

        if (allSaved) {
          return;
        }

        const role = store.state.roleName;
        const relation = store.state.keeperRVORelationNumber;
        const associationId = store.state.associationAdminAssociationId;

        const cancelSource = axios.CancelToken.source();

        store.commit("rvoSync/START_SYNC", cancelSource);

        const params = role === "Keeper"
          ? {
              rVORelationNumber: relation,
              inrelato: "Eartags for newborn (RVO/I&R)"
            }
          : {
              associationId: associationId,
              inrelato: "Eartags for newborn (RVO/I&R)"
            };

        const response = await axios.get("/rvo/syncrvoeartags", {
          params: params,
          cancelToken: cancelSource.token
        });

        store.commit("rvoSync/STOP_SYNC");

        const result = response.data;

        state.noEartagAvailable = !result.success || result.count === 0;
        state.rVOMessage = t("rvo." + result.message);        

        const hasUnsaved = animalForms.value.some(a => !a.isSaved);

        if (!hasUnsaved) {
          await fetchNewBornData(id);
        }       
      } catch {
        store.commit("rvoSync/STOP_SYNC");
        showError(t("errors.Something_went_wrong"));
      }
    };

    const submitSingleAnimal = async (index) => {
      const animal = animalForms.value[index];

      const rowValid = await v$.value[index].$validate();
      if (!rowValid) return;

      try {        
        animal.isProcessing = true;
        
        store.commit('setShowLoading', true);
        
        const payload = {
          matingId: state.matingId,
          animal: {
            tagNumber: animal.tagNumber,
            tagWorkNumber: animal.tagWorkNumber,
            genderId: animal.genderId,
            name: animal.name,
            colorId: animal.colorId
          }
        };

        const res = await axios.post("/animal/addoffspring", payload);

        const addoffspringResponse = res.data;

        if (!addoffspringResponse?.success) {
          showError(t("errors.Offspring_added_to_UBN_failed"));
          return;
        }

        const createdAnimal = addoffspringResponse.data;

        if (!createdAnimal) {
          showError(t("errors.Offspring_added_to_UBN_failed"));
          return;
        }

        animal.animalId = createdAnimal.id;
        animal.animalWorkNumber = createdAnimal.animalNumber;
        animal.tagNumber = createdAnimal.tagNumber;

        store.commit('setShowLoading', false);

        const relation = store.state.keeperRVORelationNumber;

        const birthPayload = {
          rvoRelationNumber: relation, 
          animalUbn: store.state.keeperUBN, 
          animalId: createdAnimal.id, 
          animalWorkNumber: animal.animalWorkNumber, 
          newTagWorkNumber: createdAnimal.animalNumber, 
          animalGender: createdAnimal.genderId, 
          birthDate: new Date(createdAnimal.birthDate).toISOString()
        };

        const rvoRes = await axios.get("/rvo/syncrvobirth", {
          params: birthPayload
        });

        if (!rvoRes.data?.success) {

          store.commit('setShowLoading', true);
          // ROLL BACK CREATED ANIMAL
          try {
            const payload = {
              animalId: createdAnimal.id
            };
            await axios.post("/animal/revertOffspring", payload);
          } catch (e) {
            console.error("Rollback failed", e);
          }
          
          store.commit('setShowLoading', false);

          showError(rvoRes.data.message);
          return;
        }

        animal.isSaved = true;
        showSuccess(t("errors.Offspring_added_to_UBN_successfully"));

        await syncRVOEartag(state.matingId);

      } catch (err) {
        console.error(err);
        showError(t("errors.Something_went_wrong"));
      } finally {
        animal.isProcessing = false;
        store.commit('setShowLoading', false);
      }
    };


    const onCancel = () => router.push("/birth");
    
    onMounted(async () => {
      await nextTick();
      try {        
        store.commit('setShowLoading', true);

        const id = route.params.id;

        // Step 1 - Get current mating and offspring status
        const res = await axios.get(`/animal/offspringStatus/${id}`);

        const apiData = res.data.data;

        const offspringAdded = apiData.offspringAdded;
        const animals = apiData.animals || [];
        const noOfBirth = apiData.noOfBirth || 0;
        const noOfDeath = apiData.noOfDeath || 0;

        const totalAlive = Math.max(0, noOfBirth - noOfDeath);

        // FIRST load reference data (genders + colours)
        await fetchOptions();

        state.matingId = id;

        // Step 2 - If everything already added, just display saved values
        if (offspringAdded) {
          animalForms.value = animals.map(a => ({
            tagId: "",
            tagNumber: a.tagNumber,
            tagWorkNumber: a.animalNumber,
            genderId: a.genderId,
            name: a.name,
            colorId: a.colorId,
            isSaved: true,
            isProcessing: false
          }));

          return; // IMPORTANT: stop here – no RVO call needed
        }

        // Step 3 - Load saved animals
        let savedCount = 0;

        if (animals.length > 0) {
          savedCount = animals.length;

          animalForms.value = animals.map(a => ({
            tagId: "",
            tagNumber: a.tagNumber,
            tagWorkNumber: a.animalNumber,
            genderId: a.genderId,
            name: a.name,
            colorId: a.colorId,
            isSaved: true,
            isProcessing: false
          }));
        }

        // Step 4 - Add empty forms for remaining lambs
        const remaining = totalAlive - savedCount;

        for (let i = 0; i < remaining; i++) {
          animalForms.value.push(createAnimalForm());
        }

        store.commit('setShowLoading', false);

        // Step 5 - Call RVO sync ONLY if there are unsaved animals
        if (remaining > 0) {
          await syncRVOEartag(id);
        }

      } catch (e) {
        console.error(e);
        showError(t("errors.Something_went_wrong"));
      }
    });


    return {
      state,
      animalForms,
      v$,
      availableTagsForRow,
      submitSingleAnimal,
      onCancel,
      isSyncing,
      progress,
    };
  },
};
</script>

<style scoped>
    .banner-slide-enter-active,
    .banner-slide-leave-active {
        transition: all 0.8s ease;
        overflow: hidden;
    }

    .banner-slide-enter-from,
    .banner-slide-leave-to {
        opacity: 0;
        max-height: 0;
        margin-bottom: 0;
    }

    .banner-slide-enter-to,
    .banner-slide-leave-from {
        opacity: 1;
        max-height: 200px; /* large enough to fit banner */
        margin-bottom: 1rem;
    }
</style>